import type { BreakoutSettings, PlatformAdapter, PlatformContext, PlatformRoom, PlatformRoomMember, RoomChangeEvent, RoomListing } from '@zrc/core';
import { PlatformError } from '@zrc/core';
import type { LiveParticipant, RuntimeId } from '@zrc/core';
import { setZoomAppContext } from '../api/zoomContext.js';

/**
 * Zoom Apps SDK adapter.
 *
 * The method and field names below follow the published Zoom Apps SDK
 * reference as understood when this was written. They are deliberately
 * isolated in this one file: the gate harness exercises each raw call and
 * logs the real shapes, and any mismatch is corrected here, not in the core.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type ZoomSdk = any;

export const ZOOM_CAPABILITIES = [
  'getRunningContext',
  'getUserContext',
  'getMeetingUUID',
  'getMeetingParticipants',
  'createBreakoutRooms',
  'configureBreakoutRooms',
  'openBreakoutRooms',
  'closeBreakoutRooms',
  'getBreakoutRoomList',
  'assignParticipantToBreakoutRoom',
  'onBreakoutRoomChange',
  'onParticipantChange',
  'onMyUserContextChange',
  'authorize',
  'onAuthorized',
  'getAppContext',
];

function wrap(label: string, p: Promise<unknown>): Promise<any> {
  return p.catch((e: unknown) => {
    const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);
    const code = e && typeof e === 'object' && 'code' in e ? String((e as { code: unknown }).code) : 'sdk_error';
    // Zoom's client errors are mostly not retryable (bad state, no permission); treat timeouts as retryable.
    const retryable = /timeout|busy/i.test(msg);
    throw new PlatformError(`${label}: ${msg}`, code, retryable, e);
  });
}

export class ZoomSdkAdapter implements PlatformAdapter {
  private readonly sdk: ZoomSdk;

  constructor(sdk: ZoomSdk) {
    this.sdk = sdk;
  }

  private static sdkPromise: Promise<{ sdk: ZoomSdk }> | null = null;

  /** A running account of what the Zoom toolkit did, shown on screen if the app cannot start. */
  static readonly log: string[] = [];
  private static note(line: string) {
    ZoomSdkAdapter.log.push(`${new Date().toLocaleTimeString()} ${line}`);
    ZoomSdkAdapter.onLog?.();
  }
  static onLog: (() => void) | null = null;

  /** Zoom's toolkit never answers when something is wrong, so every wait has a limit. */
  private static within(p: Promise<any>, ms: number, what: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const t = window.setTimeout(() => reject(new Error(`no answer from Zoom after ${ms / 1000} seconds (${what})`)), ms);
      p.then(
        (v) => {
          window.clearTimeout(t);
          resolve(v);
        },
        (e) => {
          window.clearTimeout(t);
          reject(e);
        },
      );
    });
  }

  /** Load and configure the SDK once; the identity step and the adapter share it. */
  private static loadSdk(): Promise<{ sdk: ZoomSdk }> {
    if (!ZoomSdkAdapter.sdkPromise) {
      ZoomSdkAdapter.sdkPromise = (async () => {
        ZoomSdkAdapter.note('Loading the Zoom toolkit…');
        const mod = await ZoomSdkAdapter.within(import('@zoom/appssdk'), 10_000, 'loading the toolkit');
        const sdk: ZoomSdk = (mod as { default?: unknown }).default ?? mod;
        ZoomSdkAdapter.note(`Toolkit loaded. Asking Zoom to start it with ${ZOOM_CAPABILITIES.length} capabilities…`);
        try {
          const cfg = await ZoomSdkAdapter.within(sdk.config({ capabilities: ZOOM_CAPABILITIES, version: '0.16' }), 8_000, 'sdk.config');
          ZoomSdkAdapter.note(`Zoom started it. Running context: ${String(cfg?.runningContext ?? 'unknown')}; client: ${String(cfg?.clientVersion ?? 'unknown')}.`);
          const unsupported = (cfg?.unsupportedApis ?? []) as string[];
          if (unsupported.length) ZoomSdkAdapter.note(`Zoom says these are not supported here: ${unsupported.join(', ')}`);
          ZoomSdkAdapter.note('Toolkit ready.');
          return { sdk };
        } catch (e) {
          ZoomSdkAdapter.note(`Full start failed: ${e instanceof Error ? e.message : JSON.stringify(e)}. Trying with only getAppContext…`);
          const cfg = await ZoomSdkAdapter.within(sdk.config({ capabilities: ['getAppContext', 'getRunningContext'], version: '0.16' }), 8_000, 'sdk.config (minimal)');
          ZoomSdkAdapter.note(`Minimal start worked. Running context: ${String(cfg?.runningContext ?? 'unknown')}. So one of the other capabilities is not enabled for this app.`);
          return { sdk };
        }
      })();
      ZoomSdkAdapter.sdkPromise.catch((e) => {
        ZoomSdkAdapter.note(`Start failed: ${e instanceof Error ? e.message : JSON.stringify(e)}`);
        ZoomSdkAdapter.sdkPromise = null; // allow a retry
      });
    }
    return ZoomSdkAdapter.sdkPromise;
  }

  /**
   * Ask Zoom who is using the app and remember the answer for every later call. Returns a plain
   * description of what happened so the page can show it if identification fails.
   */
  static async primeIdentity(): Promise<string> {
    let sdk: ZoomSdk;
    try {
      sdk = (await ZoomSdkAdapter.loadSdk()).sdk;
    } catch (e) {
      return `The Zoom toolkit did not start: ${e instanceof Error ? e.message : String(e)}`;
    }
    try {
      ZoomSdkAdapter.note('Asking Zoom who you are (getAppContext)…');
      const r = await ZoomSdkAdapter.within(sdk.getAppContext(), 8_000, 'getAppContext');
      ZoomSdkAdapter.note(`getAppContext answered (${r && r.context ? 'with an identity' : 'with nothing'}).`);
      if (r?.context) {
        setZoomAppContext(String(r.context));
        ZoomSdkAdapter.note('Zoom gave the app your identity.');
        return 'ok';
      }
      return 'Zoom returned no app context. Check that the app has the getAppContext API added in its Zoom App SDK features.';
    } catch (e) {
      return `getAppContext failed: ${e instanceof Error ? e.message : JSON.stringify(e)}. Check that the API is added under Features, Zoom App SDK.`;
    }
  }

  static async create(): Promise<ZoomSdkAdapter> {
    return new ZoomSdkAdapter((await ZoomSdkAdapter.loadSdk()).sdk);
  }

  async getContext(): Promise<PlatformContext> {
    const user = await wrap('getUserContext', this.sdk.getUserContext());
    const meeting = await wrap('getMeetingUUID', this.sdk.getMeetingUUID()).catch(() => ({ meetingUUID: 'unknown' }));
    const role = user?.role === 'host' ? 'host' : user?.role === 'coHost' || user?.role === 'co-host' ? 'cohost' : user?.role ? 'attendee' : 'unknown';
    const ctx: PlatformContext = {
      meetingId: String(meeting?.meetingUUID ?? 'unknown'),
      role,
      location: meeting?.parentUUID ? 'room' : 'main',
    };
    if (user?.participantUUID) ctx.selfRuntimeId = String(user.participantUUID) as RuntimeId;
    return ctx;
  }

  async listParticipants(): Promise<LiveParticipant[]> {
    const res = await wrap('getMeetingParticipants', this.sdk.getMeetingParticipants());
    const list: any[] = res?.participants ?? [];
    return list.map((p) => {
      const lp: LiveParticipant = {
        runtimeId: String(p.participantUUID ?? p.participantId) as RuntimeId,
        displayName: String(p.screenName ?? p.displayName ?? ''),
        location: 'main',
      };
      if (p.participantUUID) lp.persistentId = String(p.participantUUID);
      if (p.role) lp.platformRole = String(p.role);
      if (p.email) lp.email = String(p.email);
      return lp;
    });
  }

  async listRooms(): Promise<RoomListing> {
    const res = await wrap('getBreakoutRoomList', this.sdk.getBreakoutRoomList());
    const rooms: any[] = res?.rooms ?? [];
    const toMember = (p: any): PlatformRoomMember => {
      const m: PlatformRoomMember = { runtimeId: String(p.participantUUID ?? p.participantId) as RuntimeId };
      if (p.participantUUID) m.persistentId = String(p.participantUUID);
      if (p.screenName ?? p.displayName) m.displayName = String(p.screenName ?? p.displayName);
      return m;
    };
    const out: PlatformRoom[] = rooms.map((r) => {
      const members: any[] = r.participants ?? [];
      const room: PlatformRoom = {
        platformRoomId: String(r.breakoutRoomId ?? r.uuid ?? r.id),
        name: String(r.name ?? ''),
        assigned: members.map(toMember),
      };
      // If the SDK marks joined status per participant, surface it (gate question 3).
      if (members.some((m) => m.status !== undefined || m.joined !== undefined)) {
        room.joined = members.filter((m) => m.joined === true || /join/i.test(String(m.status ?? ''))).map(toMember);
      }
      return room;
    });
    const state = res?.state === 'open' || res?.state === 'opened' ? 'open' : res?.state === 'closed' ? 'closed' : 'unknown';
    return { state, rooms: out };
  }

  async createRooms(names: string[]): Promise<{ platformRoomId: string; name: string }[]> {
    await wrap('createBreakoutRooms', this.sdk.createBreakoutRooms({ numberOfRooms: names.length, assign: 'manually', names }));
    const listing = await this.listRooms();
    return names.map((name) => {
      const found = listing.rooms.find((r) => r.name === name);
      if (!found) throw new PlatformError(`Room ${name} not found after creation`, 'room_missing', false);
      return { platformRoomId: found.platformRoomId, name };
    });
  }

  async configureRooms(settings: BreakoutSettings): Promise<{ applied: Partial<BreakoutSettings>; unsupported: (keyof BreakoutSettings)[] }> {
    const options = {
      allowParticipantsChooseRoom: settings.allowSelfSelect,
      allowParticipantsReturnToMainSession: settings.allowReturnToMain,
      automaticallyMoveParticipantsIntoRooms: settings.autoMoveIntoRooms,
      automaticallyMoveParticipantsIntoMainRoom: settings.autoMoveIntoMain,
      countDown: settings.closeCountdownSeconds,
    };
    await wrap('configureBreakoutRooms', this.sdk.configureBreakoutRooms(options));
    return { applied: settings, unsupported: [] };
  }

  async assign(runtimeId: RuntimeId, platformRoomId: string | null): Promise<void> {
    const options: Record<string, unknown> = { participantUUID: runtimeId };
    if (platformRoomId !== null) options['uuid'] = platformRoomId;
    await wrap('assignParticipantToBreakoutRoom', this.sdk.assignParticipantToBreakoutRoom(options));
  }

  async openRooms(): Promise<void> {
    await wrap('openBreakoutRooms', this.sdk.openBreakoutRooms());
  }

  async closeRooms(): Promise<void> {
    await wrap('closeBreakoutRooms', this.sdk.closeBreakoutRooms());
  }

  onRoomChange(cb: (e: RoomChangeEvent) => void): () => void {
    const handler = (raw: any) => {
      const action = String(raw?.action ?? raw?.type ?? '');
      const kind: RoomChangeEvent['kind'] = /open/i.test(action) ? 'opened' : /clos/i.test(action) ? 'closed' : /join|leave|move|assign/i.test(action) ? 'participant_moved' : /creat|delet|renam|updat/i.test(action) ? 'rooms_changed' : 'unknown';
      cb({ kind, raw });
    };
    this.sdk.onBreakoutRoomChange(handler);
    return () => this.sdk.removeEventListener?.('onBreakoutRoomChange', handler);
  }

  onParticipantChange(cb: () => void): () => void {
    const handler = () => cb();
    this.sdk.onParticipantChange(handler);
    return () => this.sdk.removeEventListener?.('onParticipantChange', handler);
  }
}
